import { Injectable, ErrorHandler  } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environment/environment';
import { MainHttpErrorResponse } from "@core/classes/main-http-error-response.class";

/**
 * This class is used to handle all the errors in the application
 */
@Injectable()
export class GeneralErrorHandler implements ErrorHandler  {
  constructor() { 
    const sentryConfig: Sentry.BrowserOptions = {
      // Twonas Sentry configuration
      environment: environment.production ? 'production':'devel',
      dsn: environment.services.sentry.dsn,
      maxBreadcrumbs: 5,
      integrations: [
        // Registers the Replay integration,
        // which automatically captures Session Replays video
        // mutationLimit: The upper bound of mutations to process before Session Replay stops
      //   Sentry.replayIntegration({mutationLimit: 1000, stickySession: false})
      ],
      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      // replaysSessionSampleRate: 0.1,
      // replaysOnErrorSampleRate: 1.0,
      // This option is required for capturing headers and cookies.
      sendDefaultPii: true,
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1.0,
    };
    Sentry.init(sentryConfig);
  }

  /**
   * 
   * @param error the error thrown by angular / js code or http request
   * @returns no return
   */
  handleError(error: any): void {
    // Report JavaScript errors to Sentry
    if (error instanceof Error) {
      try {
        // Extract properties from the error object
        const errorName = error.name;
        const errorMessage = error.message;
        const errorStack = error.stack;
        // Capture the error with Sentry
        Sentry.captureEvent({
          message: "Error: " + errorMessage,
          extra: {
            name: errorName,
            stack: errorStack,
          },
        });
      } catch (e) {
        Sentry.captureException(error, e);
      }
      
      return;
    }

    // Report to sentry our HTTP errors
    if (error instanceof MainHttpErrorResponse) {
      const { message, data } = error;
      Sentry.captureMessage("MainHttpErrorResponse: " + message, {
        level: data.level,
        tags: data.tags,
        extra: data.extra,
      });
      return;
    }
    // Report other HTTP errors to Sentry
    if (error instanceof HttpErrorResponse) { 
        try {
          // Parse the JSON string
          const parsedError = (error?.error && typeof error?.error === 'string') ? JSON.parse(error.error) : error.error || error;
      
          // Access the message property
          const message = parsedError.message ? parsedError.message : parsedError;

          // send message to sentry
          Sentry.captureMessage("HttpErrorResponse: " + message, {
            level: 'error',
            tags: {
              module: 'GeneralErrorHandler',
              feature: 'GeneralErrorHandling'
            },
            extra: {
              errorMessage: error.message || 'No extra message provided'
            }
          });
        } catch (e) {
          Sentry.captureException(error, e);
        }
      return;
    }
    
    // any unhandled error sent it to sentry.
    Sentry.captureEvent(error);

  }

}